import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../../api/axios';
import { Plus, Trash2, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { useOrganization } from '../../hooks/useOrganization';
import { formatCurrency } from '../../utils/currency';

const InvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const quotationId = searchParams.get('quotationId');
  const isEdit = Boolean(id);
  const { organization } = useOrganization();

  const [formData, setFormData] = useState({
    quotationId: quotationId ? parseInt(quotationId) : null,
    customerId: '',
    vehicleId: '',
    vehicleNumber: '',
    vinNumber: '',
    odometerReading: '',
    branchId: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date().toISOString().split('T')[0],
    notes: '',
    termsConditions: 'Payment Terms – Payment is due within the specified due date mentioned above.\n\nLate Payment – Late payment charges may apply for overdue invoices.\n\nWarranty – Warranty applies only to parts and labor as specified in the original quotation.\n\nDisputes – Any disputes regarding this invoice must be raised within 7 days of receipt.\n\nServices Rendered – This invoice represents services and parts provided as agreed.\n\nLiability – The company is not liable for delays caused by parts unavailability or unforeseen issues.',
    discount: 0
  });

  const [items, setItems] = useState([
    { type: 'part', itemId: '', description: '', quantity: 1, price: 0, total: 0 }
  ]);

  const [customers, setCustomers] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [parts, setParts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      await fetchData();
      if (quotationId) {
        handleQuotationChange(quotationId);
      }
      if (isEdit) {
        await fetchInvoice();
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, quotationId]);

  const fetchData = async () => {
    try {
      const [customersRes, branchesRes, partsRes, servicesRes, quotationsRes, invoicesRes] = await Promise.all([
        axios.get('/api/customers'),
        axios.get('/api/branches'),
        axios.get('/api/parts'),
        axios.get('/api/services'),
        axios.get('/api/quotations'),
        axios.get('/api/invoices')
      ]);
      setCustomers(customersRes.data.customers || customersRes.data || []);
      setBranches(branchesRes.data || []);
      setParts(partsRes.data.parts || partsRes.data || []);
      setServices(servicesRes.data.services || servicesRes.data || []);
      
      // Process quotations data
      const quotationsData = quotationsRes.data.quotations || quotationsRes.data.data || quotationsRes.data || [];
      const invoicesData = invoicesRes.data.data || [];
      
      const filteredQuotations = quotationsData.filter(quotation => {
        const hasInvoice = invoicesData.some(invoice => invoice.quotationId === quotation.id);
        return (quotation.status === 'Accepted' && !hasInvoice) || (isEdit && quotation.id === formData.quotationId);
      });
      setQuotations(filteredQuotations);
    } catch (error) {
      toast.error('Failed to load form data');
    }
  };

  const _fetchQuotations = async () => {
    try {
      const [quotationsRes, invoicesRes] = await Promise.all([
        axios.get('/api/quotations'),
        axios.get('/api/invoices')
      ]);
      
      const quotationsData = quotationsRes.data.quotations || quotationsRes.data.data || quotationsRes.data || [];
      const invoicesData = invoicesRes.data.data || [];
      
      // Show only Accepted quotations that don't have invoices
      const filteredQuotations = quotationsData.filter(quotation => {
        const hasInvoice = invoicesData.some(invoice => invoice.quotationId === quotation.id);
        return (quotation.status === 'Accepted' && !hasInvoice) || (isEdit && quotation.id === formData.quotationId);
      });
      
      setQuotations(filteredQuotations);
    } catch (error) {
      setQuotations([]);
    }
  };

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`/api/invoices/${id}`);
      const invoice = response.data;
      
      if (invoice.paymentStatus === 'Paid') {
        toast.error('Cannot edit paid invoices');
        navigate('/accounts/invoices');
        return;
      }
      
      setFormData({
        quotationId: invoice.quotationId || '',
        customerId: invoice.customerId,
        vehicleId: invoice.vehicleId || '',
        vehicleNumber: invoice.vehicleNumber || '',
        vinNumber: invoice.vinNumber || '',
        odometerReading: invoice.odometerReading || '',
        branchId: invoice.branchId,
        invoiceDate: invoice.invoiceDate.split('T')[0],
        dueDate: invoice.dueDate.split('T')[0],
        notes: invoice.notes || '',
        termsConditions: invoice.termsConditions || 'Payment Terms – Payment is due within the specified due date mentioned above.\n\nLate Payment – Late payment charges may apply for overdue invoices.\n\nWarranty – Warranty applies only to parts and labor as specified in the original quotation.\n\nDisputes – Any disputes regarding this invoice must be raised within 7 days of receipt.\n\nServices Rendered – This invoice represents services and parts provided as agreed.\n\nLiability – The company is not liable for delays caused by parts unavailability or unforeseen issues.',
        discount: invoice.discount
      });
      setItems(invoice.items || [{ type: 'part', itemId: '', description: '', quantity: 1, price: 0, total: 0 }]);
    } catch (error) {
      toast.error('Failed to load invoice');
    }
  };

  const handleQuotationChange = async (quotationId) => {
    if (!quotationId) {
      setFormData({ ...formData, quotationId: '' });
      return;
    }

    try {
      const response = await axios.get(`/api/quotations/${quotationId}`);
      const quotation = response.data;
      
      setFormData({
        ...formData,
        quotationId: parseInt(quotationId),
        customerId: quotation.customerId || '',
        vehicleId: quotation.vehicleId || '',
        vehicleNumber: quotation.vehicleNumber || '',
        vinNumber: quotation.vinNumber || '',
        odometerReading: quotation.odometerReading || '',
        branchId: quotation.branchId || formData.branchId,
        notes: quotation.notes || '',
        termsConditions: quotation.termsConditions || formData.termsConditions,
        discount: quotation.discount || 0
      });

      if (quotation.items && quotation.items.length > 0) {
        // Ensure all items have proper totals calculated
        const itemsWithTotals = quotation.items.map(item => ({
          ...item,
          quantity: parseFloat(item.quantity) || 1,
          price: parseFloat(item.price) || 0,
          total: (parseFloat(item.quantity) || 1) * (parseFloat(item.price) || 0)
        }));
        setItems(itemsWithTotals);
      }

    } catch (error) {
      toast.error('Failed to load quotation details');
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    if (field === 'itemId' && value) {
      const itemId = parseInt(value);
      const selectedItem = newItems[index].type === 'part' 
        ? parts.find(p => p.id === itemId)
        : services.find(s => s.id === itemId);
      
      if (selectedItem) {
        newItems[index].description = selectedItem.name;
        newItems[index].price = parseFloat(selectedItem.price || selectedItem.costPrice || 0);
      }
    }
    
    // Always recalculate total after any change
    newItems[index].total = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].price) || 0);
    
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { type: 'part', itemId: '', description: '', quantity: 1, price: 0, total: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const discount = parseFloat(formData.discount) || 0;
    const vatPercentage = organization && organization.vatPercentage !== null ? organization.vatPercentage : 5;
    const tax = (subtotal - discount) * (vatPercentage / 100);
    const total = subtotal - discount + tax;
    
    return { subtotal, discount, tax, total, vatPercentage };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const invoiceData = {
        ...formData,
        items: items.filter(item => item.description.trim() || item.itemId)
      };

      // Convert empty strings to null for integer fields, but preserve quotationId if it exists
      if (!invoiceData.quotationId || invoiceData.quotationId === '') {
        invoiceData.quotationId = null;
      } else {
        invoiceData.quotationId = parseInt(invoiceData.quotationId);
      }
      if (!invoiceData.vehicleId || invoiceData.vehicleId === '') {
        delete invoiceData.vehicleId;
      }
      if (!invoiceData.customerId || invoiceData.customerId === '') {
        delete invoiceData.customerId;
      }
      if (!invoiceData.branchId || invoiceData.branchId === '') {
        delete invoiceData.branchId;
      }
      
      // Ensure termsConditions is included
      invoiceData.termsConditions = formData.termsConditions || '';

      if (isEdit) {
        await axios.put(`/api/invoices/${id}`, invoiceData);
        toast.success('Invoice updated successfully');
      } else {
        await axios.post('/api/invoices', invoiceData);
        toast.success('Invoice created successfully');
      }
      
      navigate('/accounts', { state: { activeTab: 'invoices' } });
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save invoice';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, tax, total, vatPercentage } = calculateTotals();

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Invoice' : 'Create Invoice'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quotation
              </label>
              {isEdit ? (
                <input
                  type="text"
                  value={quotations.find(q => q.id === parseInt(formData.quotationId))?.quotationNumber || (formData.quotationId ? `QUO-${formData.quotationId}` : 'No Quotation')}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                />
              ) : (
                <select
                  value={formData.quotationId || ''}
                  onChange={(e) => handleQuotationChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select Quotation</option>
                  {Array.isArray(quotations) && quotations.length > 0 ? quotations.map(quotation => (
                    <option 
                      key={quotation.id} 
                      value={quotation.id}
                      title={`Customer: ${quotation.customer?.name || 'N/A'} | Total: $${quotation.total || 0}`}
                    >
                      {quotation.quotationNumber || `QUO-${quotation.id}`} - {quotation.customer?.name || 'No Customer'}
                    </option>
                  )) : (
                    <option disabled>No quotations available</option>
                  )}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer *
              </label>
              <select
                value={formData.customerId || ''}
                onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select Customer</option>
                {Array.isArray(customers) && customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch *
              </label>
              <select
                value={formData.branchId || ''}
                onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select Branch</option>
                {Array.isArray(branches) && branches.map(branch => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle Number
              </label>
              <input
                type="text"
                value={formData.vehicleNumber}
                onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
                placeholder="e.g., ABC-123"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                VIN#
              </label>
              <input
                type="text"
                value={formData.vinNumber}
                onChange={(e) => setFormData({ ...formData, vinNumber: e.target.value })}
                placeholder="Vehicle Identification Number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ODO Meter
              </label>
              <input
                type="text"
                value={formData.odometerReading}
                onChange={(e) => setFormData({ ...formData, odometerReading: e.target.value })}
                placeholder="e.g., 50000 km"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Date *
              </label>
              <input
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date *
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </button>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-12 gap-3 items-center font-medium text-sm text-gray-700 border-b pb-2">
              <div className="col-span-2">Type</div>
              <div className="col-span-3">Item</div>
              <div className="col-span-2">Description</div>
              <div className="col-span-1">Qty</div>
              <div className="col-span-2">Price</div>
              <div className="col-span-1">Total</div>
              <div className="col-span-1">Action</div>
            </div>
            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-2">
                  <select
                    value={item.type}
                    onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="part">Part</option>
                    <option value="service">Service</option>
                  </select>
                </div>
                <div className="col-span-3">
                  <select
                    value={item.itemId || ''}
                    onChange={(e) => handleItemChange(index, 'itemId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select {item.type}</option>
                    {item.type === 'part' 
                      ? Array.isArray(parts) && parts.map(part => (
                          <option key={part.id} value={part.id}>
                            {part.name} - {formatCurrency(part.price || part.costPrice || 0, organization?.currency_code)}
                          </option>
                        ))
                      : Array.isArray(services) && services.map(service => (
                          <option key={service.id} value={service.id}>
                            {service.name} - {formatCurrency(service.price || 0, organization?.currency_code)}
                          </option>
                        ))
                    }
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="col-span-1">
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity || ''}
                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    placeholder="Price"
                    value={item.price || ''}
                    onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="col-span-1">
                  <input
                    type="text"
                    value={formatCurrency(item.total || 0, organization?.currency_code)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div className="col-span-1">
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-red-600 hover:text-red-900"
                    disabled={items.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 border-t pt-4">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(subtotal, organization?.currency_code)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Discount:</span>
                  <input
                    type="number"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                    className="w-20 px-2 py-1 border border-gray-300 rounded text-right"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="flex justify-between">
                  <span>Tax ({vatPercentage}%):</span>
                  <span>{formatCurrency(tax, organization?.currency_code)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span>{formatCurrency(total, organization?.currency_code)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Additional Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Terms & Conditions
              </label>
              <textarea
                value={formData.termsConditions}
                onChange={(e) => setFormData({ ...formData, termsConditions: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="8"
                placeholder="Enter terms and conditions for this invoice..."
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/accounts', { state: { activeTab: 'invoices' } })}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Invoice'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;